import React, { memo, useContext, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import AppContext from '../../AppContext';
import GrpHeader from 'Library/GrpHeader/GrpHeader';
import {
  LevelButton, SubLevelButton,
  ClientAndReportingDate, Client, ReportingDate,
} from './HomeHeader.styles';
import GrpSelect from 'Library/GrpSelect/GrpSelect';
import RegulationSelector from './RegulationSelector';

const MANAGE_SHARDS_LEVEL_LABEL = 'Manage Shards';

const HomeHeader = ({ module, moduleDisplayName }) => {
  const { state, handlers } = useContext(AppContext);
  const location = useLocation();

  // Derive selectedLevel from current URL path
  const selectedLevel = useMemo(
    () => location.pathname.split('/')[2],
    [location.pathname],
  );

  // Compute levels locally from selectedLayout — avoids round-trip to App state
  const levels = useMemo(() => {
    if (!state.selectedLayout) return [];
    const computedLevels = _.uniqBy(
      state.selectedLayout
        .filter((item) => item.module === module && item.level !== null)
        .map((x) => ({
          path: x.level,
          display_name: x.level_display_name,
          display_order: x.display_order,
        })),
      'path',
    );

    const shardManagementRoute = (state.availableRoutes || []).find((route) => (
      route.component === 'ShardManagement'
      && route.full_path?.startsWith(`/${module}/`)
    ));
    const shardManagementLevel = shardManagementRoute?.full_path?.split('/')[2] || null;

    if (shardManagementLevel && !computedLevels.some((item) => item.path === shardManagementLevel)) {
      return [...computedLevels, {
        path: shardManagementLevel,
        display_name: MANAGE_SHARDS_LEVEL_LABEL,
        display_order: Number.MAX_SAFE_INTEGER,
      }];
    }

    return computedLevels;
  }, [state.selectedLayout, state.availableRoutes, module]);

  // Compute subLevels locally
  const subLevels = useMemo(() => {
    if (!state.selectedLayout || !selectedLevel) return [];
    return _.uniqBy(
      state.selectedLayout
        .filter((item) => item.module === module
          && item.level === selectedLevel && item.sublevel !== null
          && item.sublevel_display_name !== null)
        .map((x) => ({
          path: x.sublevel,
          display_name: x.sublevel_display_name,
          api_name: x.api_name,
          display_order: x.display_order,
          parent: x.level,
          component: x.component,
        })),
      'path',
    );
  }, [state.selectedLayout, module, selectedLevel]);

  // Compute sublevel buttons synchronously during render (avoids extra render cycle)
  const buttons = useMemo(() => {
    if (subLevels.length > 5) {
      const items = subLevels.slice(0, 4).map((e) => (
        <SubLevelButton
          key={e.path}
          to={{
            pathname: `/${module}/${e.parent}/${e.path}`,
            api_name: e.api_name,
          }}
        >
          {e.display_name}
        </SubLevelButton>
      ));
      const dropdownItems = subLevels.slice(4, subLevels.length).map((e) => ({
        display: (
          <SubLevelButton
            key={e.path}
            to={{
              pathname: `/${module}/${e.parent}/${e.path}`,
              api_name: e.api_name,
            }}
          >
            {e.display_name}
          </SubLevelButton>
        ),
        value: `/${module}/${e.parent}/${e.path}`,
      }));
      const dropdown = (
        <GrpSelect
          key="dropdown-select"
          sublevel
          items={dropdownItems}
          placeholder={{
            display: 'Select Item',
            value: null,
          }}
        />
      );
      return items.concat(dropdown);
    }
    return subLevels.map((e) => (
      <SubLevelButton
        key={e.path}
        to={{
          pathname: `/${module}/${e.parent}/${e.path}`,
          api_name: e.api_name,
        }}
      >
        {e.display_name}
      </SubLevelButton>
    ));
  }, [subLevels, module]);

  const isMiFID = state.selectedRegulation?.Name === 'MiFID';
  const hiddenOverviewLevels = isMiFID ? ['narratives', 'translation'] : [];

  const levelsContent = (levels && levels
    .filter((item) => item.display_name !== null
      && !(module === 'overview' && hiddenOverviewLevels.includes(item.path)))
    .map((l) => (
    <LevelButton
      to={{ pathname: `/${module}/${l.path}` }}
      key={l.path}
    >
      {l.display_name}
    </LevelButton>
  )));

  const dateClick = () => {
    handlers.injectClientReportingIntoDrawer();
  };

  return (
    <GrpHeader
      title={moduleDisplayName}
      headerItems={levelsContent}
      subHeaderItems={buttons}
      headerControls={(
        <>
          {window.location.pathname.startsWith('/overview') && (
            <RegulationSelector
              clientCode={state.selectedClient?.code}
              onRegulationChange={handlers.setSelectedRegulation}
            />
          )}
          <ClientAndReportingDate
            onClick={dateClick}
            // size="Large"
            text={(
              <div>
                <Client>
                  {
                    !state.selectedClient.name === null ? (
                      'Select Client and Reporting Period'
                    ) : (state.selectedClient.name)
                  }
                </Client>
                <ReportingDate>
                  {
                    (state.selectedClient.name && state.selectedReportingDate)
                    && (state.selectedReportingDate.code)
                  }
                </ReportingDate>
              </div>
            )}
            // type="Tertiary"
          />
        </>
      )}
    />
  );
};

HomeHeader.propTypes = {
  module: PropTypes.string.isRequired,
  moduleDisplayName: PropTypes.string.isRequired,
};

export default memo(HomeHeader);
